import { createFileRoute } from '@tanstack/react-router'
import { requireAuth } from '@/lib/route-guards'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Pagination } from "@/components/ui/pagination"
import {
  ArrowLeft,
  FileSpreadsheet,
} from "lucide-react"
import { useWinnerListQuery, useExportWinnersExcelMutation } from '@/hooks/use-kura-queries'
import { Skeleton } from "@/components/ui/skeleton"
import { useState, useMemo } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/kura/$kuraId/winners')({
  beforeLoad: requireAuth,
  component: KuraWinnersPage
})

function KuraWinnersPage() {
  const { kuraId } = Route.useParams()
  const navigate = Route.useNavigate()

  const [pageIndex, setPageIndex] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const { data: winnerData, isLoading, error } = useWinnerListQuery(kuraId, pageIndex, pageSize)

  // Excel export mutation
  const exportExcelMutation = useExportWinnersExcelMutation()

  const handleExportExcel = async () => {
    try {
      await exportExcelMutation.mutateAsync(kuraId)
      toast.success('Excel dosyası başarıyla indirildi')
    } catch (error) {
      console.error('Excel export error:', error)
      toast.error('Excel dosyası indirilemedi')
    }
  }

  const winnerItem = winnerData?.items?.[0]
  const winners = winnerItem?.data || []
  const totalCount = winnerData?.count || 0

  // Column header mapping for better display names
  const columnHeaders = winnerItem?.columnHeaders || [];

  const settingsMap = useMemo(() => {
    const map: Record<string, string> = {};
    columnHeaders.forEach(h => {
      map[h.columnName] = h.label;
    });
    return map;
  }, [columnHeaders]);

  // Dinamik kolonları datanın key'lerinden türet
  const columns = useMemo<ColumnDef<Record<string, any>>[]>(() => {
    if (!winners.length) return [];

    const keys = Object.keys(winners[0]);
    const visibleKeys = keys;

    return visibleKeys.map((key) => ({
      accessorKey: key,
      header: settingsMap[key] || key,
      cell: ({ row }) => {
        const value = row.getValue(key);
        return <div>{value !== undefined ? String(value) : '-'}</div>;
      }
    }));
  }, [winners, settingsMap]);



  const table = useReactTable({
    data: winners,
    columns,
    pageCount: winnerData?.pages || 1,
    manualPagination: true,
    state: { pagination: { pageIndex: pageIndex - 1, pageSize } },
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater({ pageIndex: pageIndex - 1, pageSize })
        setPageIndex(newState.pageIndex + 1) // Convert back to 1-based
        setPageSize(newState.pageSize)
      } else {
        setPageIndex(updater.pageIndex + 1) // Convert back to 1-based
        setPageSize(updater.pageSize)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto space-y-6 p-4">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto space-y-6 p-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate({ to: '/kura/$kuraId/edit', params: { kuraId } })}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri Dön
        </Button>
        <div className="text-center p-12 border rounded-lg">
          <p className="text-red-600 font-medium">Veri yüklenirken hata oluştu</p>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'Bilinmeyen hata'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto space-y-6 p-4">
      {/* Back button */}
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate({ to: '/kura/$kuraId/edit', params: { kuraId } })}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri Dön
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h1 className="text-2xl font-bold">
          {winnerItem?.label || 'Kazananlar Listesi'}
        </h1>
        <div className="text-sm text-gray-600">
          Toplam: <span className="font-semibold">{totalCount}</span> kazanan
        </div>
      </div>

      {/* Export buttons */}
      <div className="flex items-center gap-2">
        <Button
          onClick={handleExportExcel}
          disabled={exportExcelMutation.isPending || totalCount === 0}
          size="sm"
          variant="outline"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          {exportExcelMutation.isPending ? 'İndiriliyor...' : "Excel'e Aktar"}
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="whitespace-nowrap">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Henüz kazanan bulunmamaktadır
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <Pagination
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalPages={winnerData?.pages || 1}
      />
    </div>
  )
}
